import { Component, OnInit, Injector, HostListener } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { scheduleI, scheduleStatus } from 'app/core/interfaces/schedule';
import { scheduleFilter } from 'app/core/data/forms.json';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { tabI } from 'app/core/interfaces/layout';
import { scheduleFHIR, encounterFHIR } from 'app/core/interfaces/encounter';
import { EncounterService } from 'app/core/http/services/encounter.service';
import { getPaymentStatusText } from 'app/core/utils/payment-status';
import { PatientScheduleDetailsComponent } from './details/schedule-details.component';
import { PaymentEncounterComponent } from './payment-encounter/payment-encounter.component';

@Component({
  selector: 'page-schedule-status',
  templateUrl: './schedule-status.component.html',
  styleUrls: ['./schedule-status.component.scss'],
})
export class PatientScheduleStatusComponent extends BaseComponent implements OnInit {
  public title = 'Status de atendimento';
  public atendimentos: scheduleI[] = [];
  public encounterType: number = 0;
  public searchEncounterControl = new FormControl('');
  public filterTipos = scheduleFilter;
  public tabs: tabI[] = [];
  public scheduleStatus: scheduleStatus[] = [
    { id: 'planned', text: 'Realização pendente', color: 'red', icon: 'error' },
    { id: 'finished', text: 'Confirmado', color: 'blue', icon: 'check_circle' },
    { id: 'in-progress', text: 'Em atendimento', color: 'orange', icon: 'pending' },
    { id: 'isByOrder', text: 'Ordem de chegada', color: 'gray', icon: 'error' },
    { id: 'cancelled', text: 'Cancelado', color: 'red', icon: 'error' },
  ];

  public encounters: any[] = [];
  public schedules: any[] = [];
  public encounterFHIR: any[] = [];
  public scheduleFHIR: any[] = [];
  public patientUser: any = {};
  public itemsPerPage = 10;
  public currentPage: { [key: number]: number } = {};

  public autocompleteSuggestions: string[] = [];
  public showAutocompletePanel: boolean = false;
  public showAutocompleteHint: boolean = false;

  constructor(
    private injector: Injector,
    private encounterService: EncounterService,
  ) {
    super(injector);
    this.initTabs();
  }

  ngOnInit(): void {
    this.patientUser = this.currentUser.user;
    this.getEncounters();
    this.setupAutocomplete();
  }

  setupAutocomplete() {
    this.searchEncounterControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((searchTerm: any) => {
      this.updateAutocompleteSuggestions(searchTerm);
    });
  }

  updateAutocompleteSuggestions(searchTerm: string) {
    if (!searchTerm || searchTerm.length < 3) {
      this.autocompleteSuggestions = [];
      this.showAutocompletePanel = false;
      this.showAutocompleteHint = searchTerm.length > 0 && searchTerm.length < 3;
      return;
    }

    const term = searchTerm.toLowerCase();
    const suggestions = new Set<string>();

    this.encounters.forEach((encounter) => {
      if (encounter.practitioner?.name && encounter.practitioner.name.toLowerCase().includes(term)) {
        suggestions.add(encounter.practitioner.name);
      }
    });

    this.encounters.forEach((encounter) => {
      if (encounter.organization?.name && encounter.organization.name.toLowerCase().includes(term)) {
        suggestions.add(encounter.organization.name);
      }
    });

    this.encounters.forEach((encounter) => {
      if (encounter.name && encounter.name.toLowerCase().includes(term)) {
        suggestions.add(encounter.name);
      }
    });

    this.autocompleteSuggestions = Array.from(suggestions).slice(0, 5);
    this.showAutocompletePanel = this.autocompleteSuggestions.length > 0;
    this.showAutocompleteHint = false;
  }

  selectSuggestion(suggestion: string) {
    this.searchEncounterControl.setValue(suggestion, { emitEvent: false });
    this.showAutocompletePanel = false;
  }

  onInputFocus() {
    if (this.searchEncounterControl.value && this.searchEncounterControl.value.length >= 3) {
      this.updateAutocompleteSuggestions(this.searchEncounterControl.value);
    }
  }

  onInputBlur() {
    setTimeout(() => {
      this.showAutocompletePanel = false;
    }, 150);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const inputElement = document.querySelector('input[matInput]');
    const panelElement = document.querySelector('.autocomplete-panel');

    if (inputElement && panelElement) {
      const clickedInsideInput = inputElement.contains(event.target as Node);
      const clickedInsidePanel = panelElement.contains(event.target as Node);

      if (!clickedInsideInput && !clickedInsidePanel) {
        this.showAutocompletePanel = false;
      }
    }
  }

  public get searchEncounter(): string {
    return this.searchEncounterControl.value || '';
  }

  public get scheduleFiltro(): scheduleFHIR[] {
    return [...this.schedules];
  }

  public get atendimentosFiltro(): encounterFHIR[] {
    return [...this.encounters].filter(
      (item: any) =>
        (this.searchEncounter == '' ||
          item.practitioner.name?.toUpperCase().includes(this.searchEncounter.toUpperCase()) ||
          item.name?.toUpperCase().includes(this.searchEncounter.toUpperCase()) ||
          item.organization.name?.toUpperCase().includes(this.searchEncounter.toUpperCase()) ||
          item.patient.name.toUpperCase().includes(this.searchEncounter.toUpperCase())) &&
        (this.encounterType == 0 || (this.encounterType == 1 && item.type == 'Consulta') || (this.encounterType == 2 && item.type == 'Exame')),
    );
  }

  public get atendimentosFinalizados(): any[] {
    return this.atendimentosFiltro.filter((item: any) => item.status == 'cancelled' || item.status == 'finished');
  }

  public get atendimentosEmAberto(): any[] {
    return this.atendimentosFiltro.filter((item: any) => item.status == 'planned' || item.status == 'in-progress');
  }

  public get schedulesPendentes(): scheduleFHIR[] {
    return this.scheduleFiltro.filter((item: scheduleFHIR) => item.status == 'planned');
  }

  openPaymentEncounter(encounter: any) {
    const data = {
      title: 'Pagamento Encounter',
      param: { encounter },
    };
    this.dialog.openComponentDialog(PaymentEncounterComponent, data).subscribe({
      next: () => this.getEncounters(),
    });
  }

  openDetailsEncounter(encounter: any) {
    this.dialog
      .openComponentDialog(PatientScheduleDetailsComponent, {
        title: `Dados do atendimento - ${encounter.encounter_id}`,
        param: {
          encounterFHIR: this.selectEncounterFHIR(encounter),
          encounter,
        },
      })
      .subscribe({
        next: () => this.getEncounters(),
      });
  }

  getTotalPages(tabId: number): number {
    const total = tabId === 1 ? this.atendimentosEmAberto.length : this.atendimentosFinalizados.length;
    return Math.ceil(total / this.itemsPerPage);
  }

  getTextStatus(statusCode: any, isByOrder?: boolean) {
    const adjustedStatusCode = isByOrder && statusCode === 'planned' ? 'isByOrder' : statusCode;
    return this.scheduleStatus.find((item: any) => item.id === adjustedStatusCode)?.text;
  }

  getColorStatus(statusCode: any, isByOrder?: boolean, statusPayment?: string) {
    const adjustedStatusCode = isByOrder && statusCode === 'planned' ? 'isByOrder' : statusCode;
    if (adjustedStatusCode === 'planned' && statusPayment === 'PAID') return 'green';
    if (adjustedStatusCode === 'finished') return 'blue';
    return this.scheduleStatus.find((item: any) => item.id === adjustedStatusCode)?.color;
  }

  getStatusPaymentText(statusPayment?: string): string {
    return getPaymentStatusText(statusPayment);
  }

  isPaymentActionAvailable(encounter: any): boolean {
    const validPaymentMethods = ['PIX', 'CREDIT_CARD', 'PHYSICAL'];
    const statusPayment = encounter?.status_payment;

    return (
      encounter?.status === 'planned' &&
      statusPayment !== 'PAID' &&
      validPaymentMethods.includes(encounter?.paymentMethod || 'PHYSICAL') &&
      this.isEncounterApprovedToPaid(encounter)
    );
  }

  getEncounters() {
    const patient_id = this.patientUser.id;
    this.encounterService.listByPatient(patient_id).subscribe({
      next: (resEncounter: any) => {
        this.encounterFHIR = resEncounter;
        this.encounters = this.transformObjectArray(resEncounter);
        this.tabs.forEach((tab) => {
          if (!this.currentPage[tab.id]) {
            this.currentPage[tab.id] = 1;
          }
        });
      },
      error: (err) => console.log('error buscar encounter', err),
    });
  }

  isEncounterApprovedToPaid(encounter: any) {
    const value_agreement = parseFloat(encounter?.value_agreement ?? '0');
    if (encounter.scheduling_method == 'PARTICULAR') return true;
    return encounter.agreement_approved && value_agreement > 0;
  }

  selectEncounterFHIR(encounterSelected: any) {
    return this.encounterFHIR.find((encounter) => encounter.encounter_id == encounterSelected.encounter_id);
  }

  getContent(tabId: number) {
    const allItems = tabId === 1 ? this.atendimentosEmAberto : this.atendimentosFinalizados;
    const page = this.currentPage[tabId] ?? 1;
    const start = (page - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return allItems.slice(start, end);
  }

  changePage(tabId: number, direction: 'next' | 'prev') {
    const totalItems = tabId === 1 ? this.atendimentosEmAberto.length : this.atendimentosFinalizados.length;
    const totalPages = Math.ceil(totalItems / this.itemsPerPage);
    const current = this.currentPage[tabId] ?? 1;

    if (direction === 'next' && current < totalPages) {
      this.currentPage[tabId] = current + 1;
    }

    if (direction === 'prev' && current > 1) {
      this.currentPage[tabId] = current - 1;
    }
  }

  initTabs() {
    this.tabs = [
      { id: 1, title: 'Agendados', type: 'encounter' },
      { id: 2, title: 'Finalizados', type: 'encounter' },
    ];
  }

  transformObjectArray(input: any[]): any[] {
    return input.map((item) => {
      const organization = {
        name: item.serviceProvider.display,
        organization_id: item.serviceProvider.code,
      };
      const practitioner = {
        name: item.participant[0].display,
        practitioner_id: item.participant[0].code,
      };
      const patient = {
        name: item.subject.display,
        patient_id: item.subject.code,
      };
      const servicetype_name = item.serviceType ? item.serviceType[0].coding[0].display : undefined;
      const servicecategory_name = item.serviceCategory ? item.serviceCategory[0].coding[0].display : undefined;

      return {
        encounter_id: item.encounter_id || null,
        name: item.name,
        type: item.type,
        status: item.status,
        paymentMethod: item.paymentMethod,
        specialty: item.specialty ? item.specialty[0].coding[0].display : undefined,
        servicetype_name,
        servicecategory_name,
        organization,
        practitioner,
        patient,
        plannedStartDate: item.plannedStartDate,
        plannedEndDate: item.plannedEndDate,
        diagnosis: item.diagnosis,
        status_payment: item.status_payment,
        confirmed_at: item.confirmed_at,
        value: item.value,
        byOrderInfo: item.byOrderInfo,
        scheduling_method: item.scheduling_method,
        health_plan: item.health_plan,
        value_agreement: item.value_agreement,
        agreement_approved: item.agreement_approved,
        content: item.content,
      };
    });
  }
}
